import { useMemo } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import {
  collection,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useAuth } from '../../auth/hooks/useAuth';
import type {
  CostEstimate,
  CostEstimateInput,
  CostEstimateItem,
  SupplierBidRequest,
} from '@elka/types';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import { calculateLaborCost, calculateFinalPrice } from '@elka/utils';
import { calculateChapterAwareTargetPrice } from '../../pricing-workspace/lib/markup';
import { updateDealQuotingStatus } from '@/lib/updateDealQuotingStatus';
import { allocateSequentialNumber } from '@/lib/allocateSequentialNumber';

interface UseCostEstimatesOptions {
  subscribe?: boolean;
}

type SelectedPriceMap = CostEstimate['selectedPrices'];

export function getItemEffectiveMaterialUnitCost(
  item: CostEstimateItem,
  selectedPrices?: SelectedPriceMap
): number {
  const selectedPrice = selectedPrices?.[item.itemId]?.price;
  if (typeof selectedPrice === 'number' && selectedPrice > 0) {
    return selectedPrice;
  }

  if (item.priceEntries?.length) {
    const cheapestPriceEntry = item.priceEntries.reduce<
      NonNullable<CostEstimateItem['priceEntries']>[number] | null
    >((best, entry) => {
      if (entry.price <= 0) return best;
      if (!best) return entry;
      return entry.price < best.price ? entry : best;
    }, null);
    if (cheapestPriceEntry) return cheapestPriceEntry.price;
  }

  // Self-authored (no-boq) cost typed directly by us — lowest-priority source, used
  // when there are no supplier selections/entries (the whole self-authored flow).
  if (typeof item.knownUnitCost === 'number' && item.knownUnitCost > 0) {
    return item.knownUnitCost;
  }

  return 0;
}

export function calculateMaterialsCost(
  items: CostEstimateItem[],
  selectedPrices?: SelectedPriceMap
): number {
  return items.reduce((sum, item) => {
    if (item.isHeader) return sum;
    const unitCost = getItemEffectiveMaterialUnitCost(item, selectedPrices);
    return sum + unitCost * item.quantity;
  }, 0);
}

/** Unit cost + supplier display name for quote line items (syncs matrix `selectedPrices` into תמחור). */
export function getItemEffectiveMaterialForQuoteLine(
  item: CostEstimateItem,
  selectedPrices?: SelectedPriceMap
): { unitCost: number; supplierName: string } {
  if (item.isHeader) {
    return { unitCost: 0, supplierName: '' };
  }

  const selected = selectedPrices?.[item.itemId];
  if (selected && typeof selected.price === 'number' && selected.price > 0) {
    return {
      unitCost: selected.price,
      supplierName: selected.supplierName?.trim() ?? '',
    };
  }

  if (item.priceEntries?.length) {
    const cheapest = item.priceEntries.reduce<
      NonNullable<CostEstimateItem['priceEntries']>[number] | null
    >((best, entry) => {
      if (entry.price <= 0) return best;
      if (!best) return entry;
      return entry.price < best.price ? entry : best;
    }, null);
    if (cheapest) {
      return { unitCost: cheapest.price, supplierName: cheapest.supplierName };
    }
  }

  return { unitCost: 0, supplierName: '' };
}

export function useCostEstimates({ subscribe = true }: UseCostEstimatesOptions = {}) {
  const { user } = useAuth();

  const estimatesQuery = useMemo(
    () =>
      query(
        collection(db, ...splitPath(COLLECTIONS.quotes.costEstimates)),
        orderBy('createdAt', 'desc')
      ),
    []
  );

  const [snapshot, loading, error] = useCollection(subscribe ? estimatesQuery : null);

  const costEstimates =
    (snapshot?.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as CostEstimate[]) || [];

  const getEstimateById = async (id: string): Promise<CostEstimate | null> => {
    const fromSnapshot = costEstimates.find((estimate) => estimate.id === id);
    if (fromSnapshot) return fromSnapshot;

    const estimateRef = doc(db, ...splitPath(COLLECTIONS.quotes.costEstimates), id);
    const estimateDoc = await getDoc(estimateRef);
    if (!estimateDoc.exists()) return null;

    return { id: estimateDoc.id, ...estimateDoc.data() } as CostEstimate;
  };

  const generateEstimateNumber = () =>
    allocateSequentialNumber([...splitPath(COLLECTIONS.quotes.counters), 'costEstimates'], 'ע');

  const generateBidNumber = () =>
    allocateSequentialNumber([...splitPath(COLLECTIONS.quotes.counters), 'bidRequests'], 'ב');

  const computeCosts = (
    items: CostEstimateItem[],
    laborDays: number,
    dayRate: number,
    marginPercent: number,
    equipmentTotalCost: number,
    selectedPrices?: SelectedPriceMap,
    opts?: {
      marginPercentByChapter?: CostEstimate['marginPercentByChapter'];
      pricingChapterOverrides?: CostEstimate['pricingChapterOverrides'];
      quoteType?: CostEstimate['quoteType'];
    }
  ) => {
    const materialsCost = calculateMaterialsCost(items, selectedPrices);
    const laborCost = calculateLaborCost(laborDays, dayRate);
    const totalCost = materialsCost + laborCost + equipmentTotalCost;
    // Target respects per-section margins (sub-contractor sections can carry a lower %), using the
    // same effective unit cost (selectedPrices → priceEntries fallback) as materialsCost so the two
    // stay consistent. boq-with-prices keeps the flat formula (its items aren't marked up).
    const targetPrice =
      totalCost <= 0
        ? 0
        : opts?.quoteType === 'boq-with-prices'
          ? calculateFinalPrice(totalCost, marginPercent)
          : calculateChapterAwareTargetPrice(
              {
                items,
                marginPercent,
                marginPercentByChapter: opts?.marginPercentByChapter,
                pricingChapterOverrides: opts?.pricingChapterOverrides,
                laborCost,
                equipmentTotalCost,
              },
              (item) => getItemEffectiveMaterialUnitCost(item, selectedPrices)
            );
    return { materialsCost, laborCost, totalCost, targetPrice };
  };

  const addCostEstimate = async (data: CostEstimateInput): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    const estimateNumber = await generateEstimateNumber();
    const { materialsCost, laborCost, totalCost, targetPrice } = computeCosts(
      data.items,
      data.laborDays,
      data.dayRate,
      data.marginPercent,
      data.equipmentTotalCost || 0,
      data.selectedPrices,
      {
        marginPercentByChapter: data.marginPercentByChapter,
        pricingChapterOverrides: data.pricingChapterOverrides,
        quoteType: data.quoteType,
      }
    );

    const ref = await addDoc(collection(db, ...splitPath(COLLECTIONS.quotes.costEstimates)), {
      ...data,
      estimateNumber,
      materialsCost,
      laborCost,
      totalCost,
      targetPrice,
      status: data.status || 'draft',
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: serverTimestamp(),
    });

    // Update deal quoting status
    if (data.dealId) {
      await updateDealQuotingStatus(data.dealId);
    }

    return ref.id;
  };

  const updateCostEstimate = async (
    id: string,
    data: Partial<CostEstimateInput>
  ): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    const updateData: Record<string, unknown> = { ...data };

    if (
      data.items !== undefined ||
      data.laborDays !== undefined ||
      data.dayRate !== undefined ||
      data.marginPercent !== undefined ||
      data.equipmentTotalCost !== undefined ||
      data.marginPercentByChapter !== undefined
    ) {
      // Only fetch existing doc when caller provided a partial update (need missing fields)
      // Always need the existing doc now: the section-aware target depends on marginPercentByChapter /
      // pricingChapterOverrides / quoteType, which a partial update (e.g. a margin-only edit) omits.
      const existing = await getEstimateById(id);
      const items = data.items ?? existing?.items ?? [];
      const laborDays = data.laborDays ?? existing?.laborDays ?? 0;
      const dayRate = data.dayRate ?? existing?.dayRate ?? 0;
      const marginPercent = data.marginPercent ?? existing?.marginPercent ?? 30;
      const equipmentTotalCost = data.equipmentTotalCost ?? existing?.equipmentTotalCost ?? 0;
      const selectedPrices = data.selectedPrices ?? existing?.selectedPrices;
      const { materialsCost, laborCost, totalCost, targetPrice } = computeCosts(
        items,
        laborDays,
        dayRate,
        marginPercent,
        equipmentTotalCost,
        selectedPrices,
        {
          marginPercentByChapter: data.marginPercentByChapter ?? existing?.marginPercentByChapter,
          pricingChapterOverrides:
            data.pricingChapterOverrides ?? existing?.pricingChapterOverrides,
          quoteType: data.quoteType ?? existing?.quoteType,
        }
      );
      updateData.materialsCost = materialsCost;
      updateData.laborCost = laborCost;
      updateData.totalCost = totalCost;
      updateData.targetPrice = targetPrice;
    }

    updateData.updatedBy = user.displayName || user.email || 'Unknown';
    updateData.updatedAt = serverTimestamp();

    // Strip undefined values — Firestore throws on undefined
    for (const key of Object.keys(updateData)) {
      if (updateData[key] === undefined) delete updateData[key];
    }

    await updateDoc(doc(db, ...splitPath(COLLECTIONS.quotes.costEstimates), id), updateData);

    // Update deal quoting status in background — don't block the save
    const dealId = (data as { dealId?: string }).dealId;
    if (dealId) {
      updateDealQuotingStatus(dealId).catch((err) =>
        console.error('[updateCostEstimate] background status sync failed:', err)
      );
    }
  };

  /**
   * Narrow update that writes explicit Firestore field paths (dot-path keys) with
   * merge semantics, instead of replacing whole map fields. Use this for concurrent-safe
   * partial writes into nested maps (e.g. `priceSuggestions.client.<itemId>.reviewStatus`)
   * while a backend run is streaming sibling keys into the same map — a whole-field replace
   * built from a local snapshot would clobber those freshly-flushed keys (last-write-wins).
   * Pass `deleteField()` as a value to remove a nested key. `updatedAt`/`updatedBy` are stamped.
   */
  const updateCostEstimateFields = async (
    id: string,
    fields: Record<string, unknown>
  ): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    await updateDoc(doc(db, ...splitPath(COLLECTIONS.quotes.costEstimates), id), {
      ...fields,
      updatedBy: user.displayName || user.email || 'Unknown',
      updatedAt: serverTimestamp(),
    });
  };

  const deleteCostEstimate = async (id: string): Promise<void> => {
    const existing = await getEstimateById(id);
    const dealId = existing?.dealId;

    await deleteDoc(doc(db, ...splitPath(COLLECTIONS.quotes.costEstimates), id));

    // Update deal quoting status
    if (dealId) {
      await updateDealQuotingStatus(dealId);
    }
  };

  // Create a bid request from a cost estimate, store the link on the estimate
  const createBidRequest = async (
    estimate: CostEstimate
  ): Promise<{ bidRequestId: string; bidRequestNumber: string }> => {
    if (!user) throw new Error('User not authenticated');

    const bidNumber = await generateBidNumber();
    const bidRequestData = {
      bidNumber,
      projectName: estimate.projectName,
      companyId: estimate.companyId,
      companyName: estimate.companyName,
      items: estimate.items.map((item) => ({
        itemId: item.itemId || crypto.randomUUID(),
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        notes: '',
        ...(item.section ? { code: item.section } : {}),
      })),
      requestDate: new Date().toISOString().slice(0, 10),
      deadline: '',
      notes: `נוצר מעלות ${estimate.estimateNumber}`,
      status: 'draft' as const,
      supplierResponses: [],
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: serverTimestamp(),
    };

    const bidRef = await addDoc(
      collection(db, ...splitPath(COLLECTIONS.quotes.bidRequests)),
      bidRequestData
    );

    await updateDoc(doc(db, ...splitPath(COLLECTIONS.quotes.costEstimates), estimate.id), {
      bidRequestId: bidRef.id,
      bidRequestNumber: bidNumber,
      status: 'bid-sent',
      updatedBy: user.displayName || user.email || 'Unknown',
      updatedAt: serverTimestamp(),
    });

    // Update deal quoting status
    if (estimate.dealId) {
      await updateDealQuotingStatus(estimate.dealId);
    }

    return { bidRequestId: bidRef.id, bidRequestNumber: bidNumber };
  };

  const fetchLinkedBidRequest = async (
    bidRequestId: string
  ): Promise<SupplierBidRequest | null> => {
    try {
      const bidDoc = await getDoc(
        doc(db, ...splitPath(COLLECTIONS.quotes.bidRequests), bidRequestId)
      );
      if (bidDoc.exists()) {
        return { id: bidDoc.id, ...bidDoc.data() } as SupplierBidRequest;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Add supplier links (magic links) to a bid request
  const addSupplierLinksForSend = async (
    bidId: string,
    suppliers: Array<{ supplierId: string; supplierName: string; supplierEmail?: string }>
  ): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    const bidRef = doc(db, ...splitPath(COLLECTIONS.quotes.bidRequests), bidId);
    const bidDoc = await getDoc(bidRef);

    if (!bidDoc.exists()) throw new Error('בקשת מחיר לא נמצאה');

    const bidData = bidDoc.data();
    const existingLinks = bidData.supplierLinks || [];
    const existingIds = new Set(existingLinks.map((l: { supplierId: string }) => l.supplierId));

    const newLinks = suppliers
      .filter((s) => !existingIds.has(s.supplierId))
      .map((s) => ({
        supplierId: s.supplierId,
        supplierName: s.supplierName,
        ...(s.supplierEmail ? { supplierEmail: s.supplierEmail } : {}),
        token: crypto.randomUUID(),
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        status: 'pending',
      }));

    if (newLinks.length === 0) return;

    const updates: Record<string, unknown> = {
      supplierLinks: [...existingLinks, ...newLinks],
      updatedBy: user.displayName || user.email || 'Unknown',
      updatedAt: serverTimestamp(),
    };

    if (bidData.status === 'draft') {
      updates.status = 'sent';
    }

    await updateDoc(bidRef, updates);
  };

  return {
    costEstimates: subscribe ? costEstimates : [],
    loading: subscribe ? loading : false,
    error: subscribe ? error : undefined,
    addCostEstimate,
    updateCostEstimate,
    updateCostEstimateFields,
    deleteCostEstimate,
    createBidRequest,
    fetchLinkedBidRequest,
    addSupplierLinksForSend,
  };
}
